import * as forge from 'node-forge';

export function base64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64?.length || 0) % 4) % 4);
  const base64Safe = (base64 || '') + padding;
  const normalized = base64Safe.replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = typeof window !== 'undefined' ? window.atob(normalized) : forge.util.decode64(normalized);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function importAesKey(base64Key: string): Promise<{ cryptoKey: CryptoKey | null; fallbackKeyBytes?: Uint8Array }> {
  if (!base64Key) throw new Error('importAesKey: no key provided');
  const keyBytes = base64ToUint8Array(base64Key);
  const byteLen = keyBytes.length;

  if (byteLen !== 16 && byteLen !== 32) {
    const sampleStart = base64Key.slice(0, 8);
    const sampleEnd = base64Key.slice(-8);
    console.warn('importAesKey: unsupported AES key length', { byteLen, sampleStart, sampleEnd });

    if (byteLen === 24) {
      console.warn('importAesKey: detected 192-bit key, returning fallback raw bytes for software decryption');
      return { cryptoKey: null, fallbackKeyBytes: keyBytes };
    }

    throw new Error(`Unsupported AES key length: ${byteLen * 8}-bit (${byteLen} bytes). WebCrypto supports 128-bit or 256-bit AES keys.`);
  }

  try {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes as unknown as BufferSource,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    return { cryptoKey };
  } catch (err: any) {
    console.error('importAesKey failed', { err, byteLen });
    throw new Error(`Failed to import AES key: ${err?.message || err}. Key bytes: ${byteLen}`);
  }
}

export async function decryptKey(base64Encrypted: string, cryptoKey: CryptoKey | null, fallbackKeyBytes?: Uint8Array): Promise<string> {
  if (!base64Encrypted) return '';

  // If no cryptoKey but we have fallback bytes, use forge fallback
  if (!cryptoKey && fallbackKeyBytes) {
    return decryptWithForge(base64Encrypted, fallbackKeyBytes);
  }

  if (!cryptoKey) throw new Error('decryptKey: missing CryptoKey and no fallback available');

  // WebCrypto path
  if (base64Encrypted.includes(':')) {
    const [ivB64, cipherB64] = base64Encrypted.split(':', 2);
    const ivArr = base64ToUint8Array(ivB64.trim());
    const dataArr = base64ToUint8Array(cipherB64.trim());

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArr as unknown as BufferSource, tagLength: 128 },
      cryptoKey,
      dataArr as unknown as BufferSource
    );

    return new TextDecoder().decode(decryptedBuffer);
  }

  const encryptedBytes = base64ToUint8Array(base64Encrypted);
  if (encryptedBytes.length <= 12) throw new Error('encrypted payload too short to contain IV + ciphertext');

  const ivArr = encryptedBytes.slice(0, 12);
  const dataArr = encryptedBytes.slice(12);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivArr as unknown as BufferSource, tagLength: 128 },
    cryptoKey,
    dataArr as unknown as BufferSource
  );

  return new TextDecoder().decode(decryptedBuffer);
}

// Software fallback using node-forge (supports AES-192)
export async function decryptWithForge(base64Encrypted: string, keyBytes: Uint8Array): Promise<string> {
  try {
    const toBinStr = (u8: Uint8Array) => {
      let s = '';
      for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
      return s;
    };

    let ivArr: Uint8Array;
    let cipherArr: Uint8Array;
    if (base64Encrypted.includes(':')) {
      const [ivB64, cipherB64] = base64Encrypted.split(':', 2);
      ivArr = base64ToUint8Array(ivB64.trim());
      cipherArr = base64ToUint8Array(cipherB64.trim());
    } else {
      const encryptedBytes = base64ToUint8Array(base64Encrypted);
      if (encryptedBytes.length <= 12) throw new Error('encrypted payload too short');
      ivArr = encryptedBytes.slice(0, 12);
      cipherArr = encryptedBytes.slice(12);
    }

    if (cipherArr.length < 16) throw new Error('ciphertext too short to contain auth tag');
    const tagArr = cipherArr.slice(cipherArr.length - 16);
    const ciphertextOnly = cipherArr.slice(0, cipherArr.length - 16);

    const keyStr = toBinStr(keyBytes);
    const ivStr = toBinStr(ivArr);
    const cipherStr = toBinStr(ciphertextOnly);
    const tagStr = toBinStr(tagArr);

    const decipher = forge.cipher.createDecipher('AES-GCM', keyStr);
    decipher.start({ iv: forge.util.createBuffer(ivStr), tag: forge.util.createBuffer(tagStr) });
    decipher.update(forge.util.createBuffer(cipherStr));
    const ok = decipher.finish();
    if (!ok) throw new Error('forge decryption failed: authentication tag mismatch');

    const plainBin = decipher.output.getBytes();
    const out = new Uint8Array(plainBin.length);
    for (let i = 0; i < plainBin.length; i++) out[i] = plainBin.charCodeAt(i);
    return new TextDecoder().decode(out);
  } catch (err) {
    console.error('decryptWithForge error', err);
    throw err;
  }
}
